import { DndContext } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { clsx } from "clsx";
import { clone, find, findIndex } from "lodash-es";
import { darken, mix, saturate, transparentize } from "polished";
import { type CSSProperties, type FC, useMemo, useRef } from "react";
import type { AriaButtonProps } from "react-aria";
import { mergeProps, useHover } from "react-aria";
import { useDispatch, useSelector } from "react-redux";

import { SymbolIcon, Tooltip, useTooltip } from "widgets";
import { moveItemsInArray, normalize } from "widgets/utils";

import style from "../styles/TrackHeader.module.scss";

import { getCssColor } from "../../../app-utils/color.js";
import type { IPileup } from "../../../app-utils/track-shapes/track-pileup-factory.js";
import { getPileupHeight } from "../../../app-utils/track-shapes/track-utils.js";
import type { Phenotype, TrackPhenotypeConfiguration } from "../../../graphql.js";
import {
  type IMutantGenome,
  highlightMutantGenome,
  isSamePhenotypeType,
  setGenomeBrowserPhenotypeConfiguration,
  setSelectedMutantGenomeList,
} from "../../../redux/modules/genome-browser.js";
import type { IAppState } from "../../../redux/reducer.js";
import type { ReduxDispatch } from "../../../redux/redux-utils.js";
import { GREY_COLOR } from "../../../settings/colors-settings.js";
import { CANVAS_TRACK_MARGIN } from "../../../settings/genome-viewer-settings.js";

interface ProfileRowsProps {
  pileup: IPileup;
  overflow: boolean;
  mutantGenomes: IMutantGenome[];
  messageOnTopMargin?: number;
  setHoveredMutantGenomeId: (y: number) => void;
  configuredPhenotypes: TrackPhenotypeConfiguration[];
  trackType: string;
}

const PROFILE_ROW_LABEL_DEFAULT_HEIGHT = 12; // check CSS .profile_row_label

const ProfileRows: FC<ProfileRowsProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const topPosition = useMemo(() => {
    const lanePadding = props.pileup?.pileupParams.lanePadding ?? 0;
    return props.overflow
      ? CANVAS_TRACK_MARGIN + 20 // Check profileMessageHeight default in VariantTrackLoader
      : getPileupHeight(props.pileup) + lanePadding * 2 + CANVAS_TRACK_MARGIN + (props.messageOnTopMargin ?? 0);
  }, [props]);

  return (
    <div
      className={style.track_header_list}
      style={{ marginTop: topPosition }}
      onPointerLeave={() => props.setHoveredMutantGenomeId(-1)}
    >
      <DndContext
        onDragEnd={({ active, over, delta }) => {
          const genomes = clone(props.mutantGenomes);
          const oldIndex = findIndex(genomes, (g) => String(g.id) === active.id);
          const newIndex = findIndex(genomes, (g) => String(g.id) === over.id);

          if (oldIndex === newIndex) {
            // click and didn't move
            if (delta.y === 0) dispatch(highlightMutantGenome(Number(active.id)));
          } else {
            dispatch(setSelectedMutantGenomeList(moveItemsInArray(genomes, active, over)));
          }
        }}
      >
        <SortableContext items={props.mutantGenomes.map((g) => String(g.id))}>
          {props.mutantGenomes.map((mutantGenome) => (
            <ProfileRow
              key={`item-${mutantGenome.id}`}
              mutantGenome={mutantGenome}
              height={props.pileup?.pileupParams.boxHeight ?? PROFILE_ROW_LABEL_DEFAULT_HEIGHT}
              setHoveredMutantGenomeId={props.setHoveredMutantGenomeId}
              configuredPhenotypes={props.configuredPhenotypes}
              trackType={props.trackType}
            />
          ))}
        </SortableContext>
      </DndContext>
    </div>
  );
};

interface ProfileRowProps extends AriaButtonProps {
  mutantGenome: IMutantGenome;
  height: number;
  setHoveredMutantGenomeId: (y: number) => void;
  configuredPhenotypes: TrackPhenotypeConfiguration[];
  trackType: string;
}

const ProfileRow: FC<ProfileRowProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const { attributes, listeners, setNodeRef, transform, transition, isSorting } = useSortable({
    id: String(props.mutantGenome.id),
  });
  const dragStyle = transform ? { transform: `translate3d(0, ${transform.y}px, 0)`, transition } : undefined;
  const { hoverProps, isHovered } = useHover({
    onHoverChange: (isHovering) => isHovering && !isSorting && props.setHoveredMutantGenomeId(props.mutantGenome.id),
  });

  const mutantGenomePhenotypes = useSelector<IAppState, Phenotype[]>((state) => {
    return state.genomeBrowser.phenotypeValues[props.mutantGenome.id];
  });

  return (
    <div
      ref={setNodeRef}
      {...mergeProps(hoverProps, listeners, attributes)}
      style={{ height: props.height, zIndex: isSorting ? 1 : 0, ...dragStyle }}
      className={clsx(style.profile_row_label, {
        [style.highlighted]: isHovered && !isSorting,
        [style.selected]: props.mutantGenome.highlighted,
      })}
    >
      <SymbolIcon icon={"drag_indicator"} className={style.grab_icon} />
      <div className={style.profile_row_label_text}>{props.mutantGenome.name}</div>
      {props.configuredPhenotypes?.length > 0 && (
        <div className={style.profile_row_phenotypes}>
          <DndContext
            onDragEnd={({ active, over }) => {
              const phenotypes = clone(props.configuredPhenotypes);
              const oldIndex = findIndex(phenotypes, (p) => String(p.id) === active.id);
              const newIndex = findIndex(phenotypes, (p) => String(p.id) === over.id);

              if (oldIndex !== newIndex) {
                dispatch(
                  setGenomeBrowserPhenotypeConfiguration(
                    props.trackType,
                    moveItemsInArray<TrackPhenotypeConfiguration, any, any>(phenotypes, active, over),
                  ),
                );
              }
            }}
          >
            <SortableContext
              items={props.configuredPhenotypes.map((c) => String(c.id))}
              strategy={horizontalListSortingStrategy}
            >
              {props.configuredPhenotypes.map((phenotypeConfig, i) => {
                const data = find<Phenotype>(mutantGenomePhenotypes, (p) =>
                  isSamePhenotypeType(p.phenotypeTypeData, phenotypeConfig),
                );

                return (
                  <PhenotypeBullet
                    data={data}
                    config={phenotypeConfig}
                    key={`variant-track-phenotype-${data?.phenotypeTypeData.name ?? "none"}-${
                      data?.value ?? data?.numValue ?? i
                    }`}
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        </div>
      )}
    </div>
  );
};

interface PhenotypeBulletProps {
  data: Phenotype;
  config: TrackPhenotypeConfiguration;
}

const PhenotypeBullet: FC<PhenotypeBulletProps> = ({ data, config }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id: String(config.id),
  });
  const dragStyle = transform ? { transform: `translate3d( ${transform.x}px, 0, 0)`, transition } : undefined;

  const bulletStyle: CSSProperties = useMemo(() => {
    // No data: greyed background without border
    let background = transparentize(0.65, GREY_COLOR.WHITE_2);
    let borderColor = "transparent";

    if (data?.value != undefined) {
      // This is a text entry. Color may be provided by phenotype configuration or by track configuration.
      const trackConfigurationColor = config.configuredTerms?.find((term) => term.term === data.value)?.color;
      const color = trackConfigurationColor ?? data?.color;
      background = color ? getCssColor(color) : "#ffffff";
      borderColor = color ? darken(0.15, saturate(0.1, background)) : GREY_COLOR.COOL_GREY;
    } else if (data?.numValue != undefined) {
      // This is a number entry. We need to interpolate the color.
      let mixValue: number; // Between 0 and 1

      if (data.numValue < config.minValue) {
        // Bellow the range
        mixValue = 0;
      } else if (data.numValue > config.maxValue) {
        // Above the range
        mixValue = 1;
      } else {
        // Normalize from min/max to 0/1
        mixValue = normalize(data.numValue, config.minValue, config.maxValue, 0, 1);
      }

      // If a logarithmic range is selected, transform the value (with log10)
      if (config.logScale === true) {
        // Normalize again for the log10
        mixValue = Math.log10(normalize(mixValue, 0, 1, 1, 10));
      }

      // We need to invert colors as it mixes the first color to the second color.
      // The color scheme seems to always have 2 or 3 colors for the gradient.
      const colors = config.colorScheme?.colors;
      background = colors
        ? colors.length === 2
          ? mix(mixValue, colors[1], colors[0])
          : mix(
              (mixValue < 0.5 ? mixValue : mixValue - 0.5) * 2,
              colors[mixValue < 0.5 ? 1 : 2],
              colors[mixValue < 0.5 ? 0 : 1],
            )
        : "white";
      borderColor = darken(0.15, saturate(0.1, background));
    }

    return { background, borderColor };
  }, [config, data]);

  const triggerRef = useRef(null);
  const tootltipRef = useRef(null);
  const { triggerProps, tooltipProps, arrowProps, state } = useTooltip(triggerRef, tootltipRef, {
    isDisabled: !data,
    delay: 0,
  });

  return (
    <div className={style.phenotype_drag_wrapper} style={dragStyle} ref={setNodeRef} {...attributes} {...listeners}>
      <div className={style.phenotype} style={bulletStyle} ref={triggerRef} {...triggerProps} />
      {state.isOpen && (
        <Tooltip
          domProps={tooltipProps}
          arrowProps={arrowProps}
          ref={tootltipRef}
          title={data?.phenotypeTypeData.name}
          content={data?.value ?? data?.numValue}
        />
      )}
    </div>
  );
};

export { ProfileRows };
